/**
 * Discovery helpers for finding sensor units by their BLE advertised name.
 *
 * Units advertise as `<prefix>_<code>` — today `MotorCityICE_A7D3E7EC`, but the
 * prefix is expected to change (e.g. `IceMover`) and may vary by brand/tenant, so
 * it must NOT be hardcoded. The allowed prefixes are configuration (default
 * below; overridable per tenant). The advertised name is only a discovery
 * FILTER — a unit's real identity (serial, etc.) comes from reading `fff00100`
 * after connecting, so nothing downstream depends on the name format.
 */

/**
 * Default advertised-name prefixes to look for. Override per tenant (e.g. via an
 * instance setting) rather than editing this list for a one-off brand.
 */
export const DEFAULT_SENSOR_NAME_PREFIXES: ReadonlyArray<string> = [
	'MotorCityICE',
	'IceMover',
];

/** A sensor unit's advertised name split into its parts. */
export interface SensorAdvertisedNameModel {
	/** The matched prefix (e.g. `MotorCityICE`). */
	prefix: string;
	/** The per-unit code suffix after the `_` (typically 8 chars). */
	code: string;
}

function escapeRegExp(literal: string): string {
	return literal.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Parse an advertised name into `{ prefix, code }` if it matches one of the
 * allowed prefixes in `<prefix>_<code>` form, else `null`. The code is whatever
 * follows the first `_` after the prefix (length is not over-validated — the
 * firmware could change it — but it is normally an 8-character hex code).
 */
export function parseSensorAdvertisedName(
	name: string | null | undefined,
	prefixes: ReadonlyArray<string> = DEFAULT_SENSOR_NAME_PREFIXES,
): SensorAdvertisedNameModel | null {
	if (name == null || name === '') {
		return null;
	}
	for (const prefix of prefixes) {
		const match = new RegExp(`^${escapeRegExp(prefix)}_(.+)$`).exec(name);
		if (match != null) {
			return { prefix, code: match[1] ?? '' };
		}
	}
	return null;
}

/** Whether an advertised name looks like one of our sensor units. */
export function isSensorDevice(
	name: string | null | undefined,
	prefixes: ReadonlyArray<string> = DEFAULT_SENSOR_NAME_PREFIXES,
): boolean {
	return parseSensorAdvertisedName(name, prefixes) != null;
}
